/**
 * Contexto de Bloqueo de Pantalla
 *
 * Maneja el bloqueo/desbloqueo con PIN de 4 dígitos
 * Incluye detección de inactividad para auto-lock
 */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { storage } from '../utils/storage';

const LockContext = createContext();

const DEFAULT_LOCK_TIME = 5; // 5 minutos
const PIN_STORAGE_KEY = 'admin_screen_pin';
const LOCK_STATE_KEY = 'admin_screen_locked';
const LOCK_TIMESTAMP_KEY = 'admin_screen_lock_timestamp';
const AUTO_LOCK_ENABLED_KEY = 'admin_auto_lock_enabled';
const AUTO_LOCK_TIME_KEY = 'admin_auto_lock_time';

export const useLock = () => {
  const context = useContext(LockContext);
  if (!context) {
    throw new Error('useLock debe ser usado dentro de un LockProvider');
  }
  return context;
};

export const LockProvider = ({ children }) => {
  // Estado de bloqueo
  const [isLocked, setIsLocked] = useState(() => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    const lockState = storage.get(LOCK_STATE_KEY);
    const lockTimestamp = storage.get(LOCK_TIMESTAMP_KEY);

    if (!savedPin) return false;
    if (lockState !== 'true') return false;

    if (lockTimestamp) {
      const hoursSinceLock = (Date.now() - parseInt(lockTimestamp)) / (1000 * 60 * 60);
      if (hoursSinceLock > 24) {
        storage.remove(LOCK_STATE_KEY);
        storage.remove(LOCK_TIMESTAMP_KEY);
        return false;
      }
    }
    return true;
  });

  const [hasPin, setHasPin] = useState(false);

  // Configuración
  const [autoLockEnabled, setAutoLockEnabledState] = useState(() => {
    const saved = storage.get(AUTO_LOCK_ENABLED_KEY);
    return saved !== 'false'; // Default true
  });

  const [autoLockTime, setAutoLockTimeState] = useState(() => {
    const saved = storage.get(AUTO_LOCK_TIME_KEY);
    return saved ? parseInt(saved) : DEFAULT_LOCK_TIME;
  });

  // Timer ref para evitar re-renders
  const inactivityTimerRef = useRef(null);

  // Verificar si existe un PIN al cargar
  useEffect(() => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    setHasPin(!!savedPin);
  }, []);

  // Persistir configuración
  const setAutoLockEnabled = (enabled) => {
    setAutoLockEnabledState(enabled);
    storage.set(AUTO_LOCK_ENABLED_KEY, enabled);
    if (!enabled && inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  };

  const setAutoLockTime = (minutes) => {
    setAutoLockTimeState(minutes);
    storage.set(AUTO_LOCK_TIME_KEY, minutes);
  };

  // Resetear el timer de inactividad
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Solo crear nuevo timer si:
    // 1. No está bloqueado
    // 2. Tiene PIN configurado
    // 3. Auto-lock está habilitado
    if (!isLocked && hasPin && autoLockEnabled) {
      inactivityTimerRef.current = setTimeout(
        () => {
          storage.set(LOCK_STATE_KEY, 'true');
          storage.set(LOCK_TIMESTAMP_KEY, Date.now().toString());
          setIsLocked(true);
        },
        autoLockTime * 60 * 1000
      );
    }
  }, [isLocked, hasPin, autoLockEnabled, autoLockTime]);

  // Detectar actividad del usuario
  useEffect(() => {
    if (!hasPin || isLocked || !autoLockEnabled) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => resetInactivityTimer();

    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Iniciar el timer
    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [hasPin, isLocked, autoLockEnabled, resetInactivityTimer]);

  // Configurar PIN por primera vez
  const setPin = (pin) => {
    if (pin.length < 4 || pin.length > 6 || !/^\d{4,6}$/.test(pin)) {
      throw new Error('El PIN debe tener entre 4 y 6 dígitos');
    }
    storage.set(PIN_STORAGE_KEY, pin);
    setHasPin(true);
    // Habilitar auto-lock por defecto al configurar PIN
    setAutoLockEnabled(true);
  };

  // Bloquear manualmente
  const lock = () => {
    if (!hasPin) {
      return;
    }
    storage.set(LOCK_STATE_KEY, 'true');
    storage.set(LOCK_TIMESTAMP_KEY, Date.now().toString());
    setIsLocked(true);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  };

  // Intentar desbloquear con PIN
  const unlock = (pin) => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    if (pin === savedPin) {
      storage.remove(LOCK_STATE_KEY);
      storage.remove(LOCK_TIMESTAMP_KEY);
      setIsLocked(false);
      // El useEffect de actividad se encargará de reiniciar el timer
      return true;
    } else {
      return false;
    }
  };

  // Cambiar PIN (requiere PIN actual)
  const changePin = (currentPin, newPin) => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    if (currentPin !== savedPin) {
      const err = new Error('PIN actual incorrecto');
      err.code = 'INCORRECT_CURRENT_PIN';
      throw err;
    }
    if (newPin.length < 4 || newPin.length > 6 || !/^\d{4,6}$/.test(newPin)) {
      throw new Error('El nuevo PIN debe tener entre 4 y 6 dígitos');
    }
    storage.set(PIN_STORAGE_KEY, newPin);
  };

  // Eliminar PIN (desactivar seguridad)
  const removePin = () => {
    storage.remove(PIN_STORAGE_KEY);
    storage.remove(LOCK_STATE_KEY);
    storage.remove(LOCK_TIMESTAMP_KEY);
    storage.remove(AUTO_LOCK_ENABLED_KEY);
    storage.remove(AUTO_LOCK_TIME_KEY);
    setHasPin(false);
    setIsLocked(false);
    setAutoLockEnabled(false);
  };

  const value = {
    isLocked,
    hasPin,
    autoLockEnabled,
    autoLockTime,
    setPin,
    setAutoLockEnabled,
    setAutoLockTime,
    lock,
    unlock,
    changePin,
    removePin,
  };

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>;
};
